import React, { useCallback, useMemo, useState } from 'react'
import { Typography, MenuItem, TextField, Tooltip, Icon } from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { formatNumsToDollars, FONT_WEIGHTS, endDateIsBeforeOrEqualToStartDate, VISIT_TYPES } from '../../utils'
import CalendarIconButton from '../Common/Buttons/CalendarIconButton'
import { ArrowForwardIosOutlined, ArrowBackIosOutlined } from '@material-ui/icons'
import moment from 'moment'
import useSelectedHousehold from '../../hooks/useSelectedHousehold'
import useBoardingVisit from '../../hooks/useBoardingVisit'
import useDaycareVisit from '../../hooks/useDaycareVisit'
import useDayRestrictions from '../../hooks/useDayRestrictions'
import { purple } from '../../utils/colors'
import StyledDatePicker from '../Common/StyledDatePicker'
import StyledAlert from '../Common/StyledAlert'
import { STATUS_ID } from '../../utils'

const CLIENT_HAS_MULTIPLE_APPOINTMENTS_ON_THIS_DAY = 'Client has multiple appointments on this day already.'

const LightTooltip = withStyles((theme) => ({
	root: {
		backgroundColor: 'blue'
	},
	tooltip: {
		backgroundColor: theme.palette.common.white,
		color: 'rgba(0, 0, 0, 0.87)',
		boxShadow: theme.shadows[1],
		padding: '0.8rem',
		fontSize: 11
	},
	arrow: {
		backgroundColor: theme.palette.common.white
	}
}))(Tooltip)

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.8rem'
	},
	capitalize: {
		textTransform: 'capitalize'
	},
	rowWrapper: {
		display: 'flex',
		gap: '1.5625rem'
	},
	fieldWrapper: {
		display: 'flex',
		flexDirection: 'column',
		maxWidth: 420
	},
	calendarButton: {
		'& svg': {
			fill: theme.palette.primary.main
		}
	},
	calendarHeaderButtons: {
		fill: theme.palette.primary.main
	},
	datePicker: {
		margin: 0,
		'& .MuiInputLabel-filled': {
			transform: 'translate(48px, 20px) scale(1)'
		},
		'& .MuiInputLabel-shrink': {
			transform: 'translate(48px, 10px) scale(0.75)'
		}
	},
	timeText: {
		fontFamily: 'Haboro Soft',
		fontWeight: FONT_WEIGHTS.Normal,
		fontSize: '.875rem',
		marginTop: '.9rem'
	},
	day: {
		position: 'relative'
	}
}))

const useCalendarStyles = (iconSize = '.4rem', bottom = '1px') =>
	makeStyles(() => ({
		root: {
			position: 'absolute',
			bottom,
			left: '0',
			width: '40px',
			display: 'flex',
			justifyContent: 'center',
			'& .MuiIcon-root': {
				fontSize: iconSize,
				color: purple[500],
				fill: purple[500],
				overflow: 'visible'
			}
		}
	}))

const useClickBlockStyles = makeStyles(() => ({
	root: {
		position: 'absolute',
		top: '0',
		left: '0',
		width: '100%',
		height: '100%',
		zIndex: 10000,
		backgroundColor: 'transparent'
	}
}))

const ClickBlock = ({ children }) => {
	const classes = useClickBlockStyles()
	return (
		<div
			onClick={(e) => {
				e.preventDefault()
				e.stopPropagation()
			}}
			className={classes.root}>
			{children}
		</div>
	)
}

const BellIcon = ({ hidden = false }) => {
	if (hidden) {
		return null
	}
	const classes = useCalendarStyles('0.5rem', 0)()
	return (
		<div className={classes.root}>
			<Icon className="fas fa-bell" />
		</div>
	)
}

const CircleIcon = ({ hidden = false }) => {
	if (hidden) {
		return null
	}
	const classes = useCalendarStyles()()
	return (
		<div className={classes.root}>
			<Icon className="fas fa-circle" />
		</div>
	)
}

const BoardingAppointmentForm = ({
	visit,
	onStartDateSelected,
	onEndDateSelected,
	// onSharingKennelsSelected,
	onFirstDayDaycareSelected,
	onLastDayDaycareSelected,
	onFirstDayDropOffTimeSelected,
	onLastDayPickUpTimeSelected
}) => {
	const classes = useStyles()

	const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false)
	const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false)
	const { isVipOrPetCareOnDate } = useSelectedHousehold()
	const { daycare } = useDaycareVisit()
	const { boarding } = useBoardingVisit()
	const { dayRestrictionsMap } = useDayRestrictions()

	const hasFreeEarlyDropoffOnStartDate = useMemo(() => {
		if (!visit) {
			return false
		}
		const { startDate } = visit
		const membershipOnStartDate = isVipOrPetCareOnDate(startDate, visit.facility_id)
		return membershipOnStartDate.vip || membershipOnStartDate.petcare
	}, [visit, isVipOrPetCareOnDate])

	const dropOffTimeText = useMemo(() => {
		if (!daycare || !boarding || !visit || !visit.startDate) {
			return ''
		}

		let text = 'Drop-off between '

		const { early_dropoff, first_day_daycare } = visit
		const { standard_dropoff_hours, early_dropoff_hours } = daycare
		const { dropoff_begins, dropoff_deadline } = boarding

		if (!first_day_daycare) {
			text +=
				moment(dropoff_begins, 'h:mm').format('ha') + ' - ' + moment(dropoff_deadline, 'h:mm').format('ha')
			return text
		}

		if (!early_dropoff) {
			text += standard_dropoff_hours
			return text
		}

		text += early_dropoff_hours

		return text
	}, [daycare, boarding, visit])

	const pickupTimeText = useMemo(() => {
		if (!daycare || !boarding || !visit || !visit.endDate) {
			return ''
		}

		let text = 'Pick-up by '

		const { extended_pickup, last_day_daycare } = visit
		const { standard_pickup_time, late_pickup_standard_pickup_time } = daycare
		const { pickup_deadline } = boarding

		if (!last_day_daycare) {
			text += moment(pickup_deadline, 'h:mm').format('ha')
			return text
		}

		if (!extended_pickup) {
			text += moment(standard_pickup_time, 'h:mm').format('ha')
			return text
		}

		text += moment(late_pickup_standard_pickup_time, 'h:mm').format('ha')

		return text
	}, [daycare, boarding, visit])

	const dropoffDaycareMenuItems = useMemo(() => {
		return [
			<MenuItem key="drop-off-daycare-no" value={false} className={classes.capitalize}>
				No, no daycare on drop-off day
			</MenuItem>,
			<MenuItem key="drop-off-daycare-yes" value={true} className={classes.capitalize}>
				Yes, daycare on drop-off day
			</MenuItem>
		]
	}, [])

	const pickupDaycareMenuItems = useMemo(() => {
		return [
			<MenuItem key="pickup-daycare-no" value={false} className={classes.capitalize}>
				No, no daycare on pick-up day
			</MenuItem>,
			<MenuItem key="pickup-daycare-yes" value={true} className={classes.capitalize}>
				Yes, daycare on pick-up day
			</MenuItem>
		]
	}, [])

	const dropoffDaycareTimeMenuItems = useMemo(() => {
		if (!visit || !daycare) {
			return []
		}

		const { extended_pickup, first_day_daycare } = visit

		if (!first_day_daycare) {
			return [
				<MenuItem key="drop-off-daycare" value={false} className={classes.capitalize}>
					Standard
				</MenuItem>
			]
		}

		let earlyPrice = formatNumsToDollars(daycare.early_dropoff_fee)

		if (hasFreeEarlyDropoffOnStartDate) {
			earlyPrice = formatNumsToDollars(0)
		}

		return [
			<MenuItem
				disabled={!first_day_daycare}
				key="drop-off-daycare"
				value={false}
				className={classes.capitalize}>
				Standard - Free
			</MenuItem>,
			<MenuItem
				disabled={!first_day_daycare}
				key="drop-off-daycare"
				value={true}
				className={classes.capitalize}>
				{`Early Drop-off - ${earlyPrice}`}
			</MenuItem>
		]
	}, [hasFreeEarlyDropoffOnStartDate, daycare, visit])

	const pickupDaycareTimeMenuItems = useMemo(() => {
		if (!visit || !daycare) {
			return []
		}

		const { last_day_daycare } = visit

		if (!last_day_daycare) {
			return [
				<MenuItem key="drop-off-daycare" value={false} className={classes.capitalize}>
					Standard
				</MenuItem>
			]
		}

		const extendedPrice = formatNumsToDollars(0)

		return [
			<MenuItem
				disabled={!last_day_daycare}
				key="pickup-daycare-standard"
				value={false}
				className={classes.capitalize}>
				Standard - Free
			</MenuItem>,
			<MenuItem
				disabled={!last_day_daycare}
				key="pickup-daycare-extended"
				value={true}
				className={classes.capitalize}>
				{`Late Pick-up - ${extendedPrice}`}
			</MenuItem>
		]
	}, [daycare, visit])

	const handleOnStartDateSelected = useCallback(
		(date) => {
			if (onStartDateSelected) {
				onStartDateSelected(date)
			}
		},
		[onStartDateSelected]
	)

	const handleOnEndDateSelected = useCallback(
		(date) => {
			if (onEndDateSelected) {
				onEndDateSelected(date)
			}
		},
		[onEndDateSelected]
	)

	const handleOnFirstDayDaycareSelected = useCallback(
		(e) => {
			if (onFirstDayDaycareSelected) {
				onFirstDayDaycareSelected(e.target.value)
			}
		},
		[onFirstDayDaycareSelected]
	)

	const handleOnLastDayDaycareSelected = useCallback(
		(e) => {
			if (onLastDayDaycareSelected) {
				onLastDayDaycareSelected(e.target.value)
			}
		},
		[onLastDayDaycareSelected]
	)

	const handleOnFirstDayDropOffTimeSelected = useCallback(
		(e) => {
			if (onFirstDayDropOffTimeSelected) {
				onFirstDayDropOffTimeSelected(e.target.value)
			}
		},
		[onFirstDayDropOffTimeSelected]
	)

	const handleOnLastDayPickUpTimeSelected = useCallback(
		(e) => {
			if (onLastDayPickUpTimeSelected) {
				onLastDayPickUpTimeSelected(e.target.value)
			}
		},
		[onLastDayPickUpTimeSelected]
	)

	const dateError = useMemo(() => {
		const { startDate, endDate } = visit
		return endDateIsBeforeOrEqualToStartDate({
			startDate: moment(startDate),
			endDate: moment(endDate)
		})
	}, [visit])

	const renderDay = useCallback(
		(day, selectedDate, isInCurrentMonth, dayComponent, dateValue) => {
			// console.log('day', day, selectedDate, isInCurrentMonth, dayComponent, dateValue)

			const date = day.format('YYYY-MM-DD')
			const hasRestriction = dayRestrictionsMap.get(date)

			// Initialize these to false
			let isDropOffPickupRestricted = false // date falls in restricted_pick_drop_dates -> SHOULD THIS DAY BE DISABLED?
			let hasSurcharge = false // surcharge_dates included this date in the array and the surcharge fee is greater than 0
			let isOvercap = false // over_cap was true on this boarding holiday and this date fell within the range of dates
			let hasBoardingApptAlready = false // will be set to true if this date exists in the household array and is a boarding type
			let tooltipText = '' // empty unless a tooltipText exists

			if (hasRestriction) {
				const { boarding, household } = hasRestriction
				if (household && household.length > 0) {
					hasBoardingApptAlready = household.some((visit) => {
						const { type, status } = visit
						return type === VISIT_TYPES.Boarding && status.id < STATUS_ID.Completed
					})
					tooltipText +=
						household.length > 1 ? CLIENT_HAS_MULTIPLE_APPOINTMENTS_ON_THIS_DAY : household[0].tooltip
				}

				if (boarding) {
					const [boardingRestrictions] = boarding
					isDropOffPickupRestricted = boardingRestrictions.isDropOffPickupRestricted
					hasSurcharge = boardingRestrictions.surchargeFee > 0
					isOvercap = boardingRestrictions.isOvercap
					// only over-write the tooltip if its empty, that way the tooltip related to a potential existing appointment takes precedence
					if (tooltipText === '') {
						tooltipText += boardingRestrictions.tooltip
					}
					if (isOvercap) {
						tooltipText += "Overcap - Can't board on this day"
					}
				}
			}

			const showBell = isDropOffPickupRestricted || hasSurcharge || isOvercap
			const showCircle = hasBoardingApptAlready

			let className = classes.day

			const { props } = dayComponent
			let revisedProps = { ...props }

			// the initial date value is null, normally the library will auto select the current date, but this is not desired
			if (dateValue === null) {
				revisedProps.selected = false
			}

			// if the client already has a boarding appt this day, or the holiday is overcap, then fully disable the day
			if (isOvercap || hasBoardingApptAlready) {
				revisedProps.disabled = true
			}

			const updatedComponent = {
				...dayComponent,
				props: {
					...revisedProps
				}
			}

			let dayElem = (
				<div className={className}>
					{/* if the day is disabled, add a ClickBlock element in the top z index to steal the clicks. */}
					{revisedProps.disabled ? <ClickBlock /> : null}
					{updatedComponent}
					{showBell && !hasBoardingApptAlready ? <BellIcon hidden={props.hidden} /> : null}
					{showCircle ? <CircleIcon hidden={props.hidden} /> : null}
				</div>
			)

			if (tooltipText > '') {
				dayElem = (
					<LightTooltip leaveDelay={100} title={tooltipText}>
						{dayElem}
					</LightTooltip>
				)
			}
			return dayElem
		},
		[dayRestrictionsMap, visit]
	)

	const daycareUnavailableOnStartDate = useMemo(() => {
		if (!visit || !visit.startDate) {
			return false
		}
		const { startDate } = visit

		const hasRestriction = dayRestrictionsMap.get(startDate.format('YYYY-MM-DD'))
		if (!hasRestriction || hasRestriction.daycareAllowed === undefined) {
			return false
		}
		const { daycareAllowed } = hasRestriction
		return !daycareAllowed
	}, [dayRestrictionsMap, visit])

	const daycareUnavailableOnEndDate = useMemo(() => {
		if (!visit || !visit.endDate) {
			return false
		}
		const { endDate } = visit
		const hasRestriction = dayRestrictionsMap.get(endDate.format('YYYY-MM-DD'))
		if (!hasRestriction || hasRestriction.daycareAllowed === undefined) {
			return false
		}
		const { daycareAllowed } = hasRestriction
		return !daycareAllowed
	}, [dayRestrictionsMap, visit])

	return (
		<div className={classes.root}>
			<div className={classes.rowWrapper}>
				<div className={classes.fieldWrapper}>
					<Typography variant="overline">Select A Start Date</Typography>
					<StyledDatePicker
						disableToolbar
						open={isStartCalendarOpen}
						onOpen={() => setIsStartCalendarOpen(true)}
						onClose={() => setIsStartCalendarOpen(false)}
						InputProps={{
							startAdornment: <CalendarIconButton onClicked={setIsStartCalendarOpen} />
						}}
						variant="inline"
						format="MM/DD/YYYY"
						margin="normal"
						id="start-date"
						label={'Start Date'}
						inputVariant="filled"
						renderDay={(day, selectedDate, isInCurrentMonth, dayComponent) =>
							renderDay(day, selectedDate, isInCurrentMonth, dayComponent, visit.startDate)
						}
						value={visit.startDate}
						onChange={handleOnStartDateSelected}
						className={classes.datePicker}
						disablePast
						rightArrowIcon={<ArrowForwardIosOutlined className={classes.calendarHeaderButtons} />}
						leftArrowIcon={<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />}
					/>
				</div>
				<div className={classes.fieldWrapper}>
					<Typography variant="overline">Select An End Date</Typography>
					<StyledDatePicker
						disableToolbar
						open={isEndCalendarOpen}
						onOpen={() => setIsEndCalendarOpen(true)}
						onClose={() => setIsEndCalendarOpen(false)}
						InputProps={{
							startAdornment: <CalendarIconButton onClicked={setIsEndCalendarOpen} />
						}}
						variant="inline"
						format="MM/DD/YYYY"
						margin="normal"
						id="end-date"
						label="End Date"
						inputVariant="filled"
						renderDay={(day, selectedDate, isInCurrentMonth, dayComponent) =>
							renderDay(day, selectedDate, isInCurrentMonth, dayComponent, visit.endDate)
						}
						value={visit.endDate}
						onChange={handleOnEndDateSelected}
						className={classes.datePicker}
						disablePast
						error={dateError}
						helperText={
							dateError ? <span>End date cannot be before or equal to start date</span> : null
						}
						rightArrowIcon={<ArrowForwardIosOutlined className={classes.calendarHeaderButtons} />}
						leftArrowIcon={<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />}
					/>
				</div>
			</div>
			<div className={classes.rowWrapper}>
				<div className={classes.fieldWrapper}>
					<Typography variant="overline">Drop-off Daycare</Typography>
					<div>
						<TextField
							id="drop-off-daycare"
							variant="filled"
							select
							disabled={!visit.startDate || daycareUnavailableOnStartDate}
							label="Drop-off Daycare"
							className={classes.capitalize}
							value={visit.first_day_daycare || false}
							onChange={handleOnFirstDayDaycareSelected}>
							{dropoffDaycareMenuItems}
						</TextField>
					</div>
				</div>
				<div className={classes.fieldWrapper}>
					<Typography variant="overline">Pick-up Daycare</Typography>
					<div>
						<TextField
							id="pickup-daycare"
							variant="filled"
							select
							disabled={!visit.endDate || daycareUnavailableOnEndDate}
							label="Pick-up Daycare"
							className={classes.capitalize}
							value={visit.last_day_daycare || false}
							onChange={handleOnLastDayDaycareSelected}>
							{pickupDaycareMenuItems}
						</TextField>
					</div>
				</div>
			</div>
			<div className={classes.rowWrapper}>
				<div className={classes.fieldWrapper}>
					<Typography variant="overline">Drop-off Time</Typography>
					<div>
						<TextField
							id="dropoff"
							variant="filled"
							select
							label="Drop-off Time"
							className={classes.capitalize}
							disabled={!visit.first_day_daycare}
							value={visit.early_dropoff || false}
							onChange={handleOnFirstDayDropOffTimeSelected}>
							{dropoffDaycareTimeMenuItems}
						</TextField>
					</div>
					{daycareUnavailableOnStartDate ? (
						<StyledAlert
							style={{ marginTop: '.8rem' }}
							type="warning"
							text={`Daycare not available on ${visit.startDate.format('MM/DD/YYYY')}.`}
						/>
					) : null}
					{dropOffTimeText > '' ? <div className={classes.timeText}>{dropOffTimeText}</div> : null}
				</div>
				<div className={classes.fieldWrapper}>
					<Typography variant="overline">Pick-up Time</Typography>
					<div>
						<TextField
							id="pickup-time"
							variant="filled"
							select
							label="Pick-up Time"
							className={classes.capitalize}
							disabled={!visit.last_day_daycare}
							value={visit.extended_pickup || false}
							onChange={handleOnLastDayPickUpTimeSelected}>
							{pickupDaycareTimeMenuItems}
						</TextField>
					</div>
					{daycareUnavailableOnEndDate ? (
						<StyledAlert
							style={{ marginTop: '.8rem' }}
							type="warning"
							text={`Daycare not available on ${visit.endDate.format('MM/DD/YYYY')}.`}
						/>
					) : null}
					{pickupTimeText > '' ? <div className={classes.timeText}>{pickupTimeText}</div> : null}
				</div>
			</div>
		</div>
	)
}

export default BoardingAppointmentForm
